import React, { Component } from 'react';

// Components
import { GrowthCell, PercentageCell } from '../cells';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import CurrencyCell from '../cells/CurrencyCell';
import { LoadingSpinner } from '../common';
import { PerformanceDisclaimer } from '../disclaimers/comparison';

// Enums
import { PORTFOLIO_METRICS_FIELDS } from '@/enums/Portfolio';

// Redux
import { queryPortfolioMetricsById } from '@/redux/modules/metrics';

// Third-parties
import { connect } from 'react-redux';
import classNames from 'classnames';
import get from 'lodash/get';

// Types
import { PortfolioShortMetrics } from '@/redux/modules/types/metrics';
import { ReduxState } from '@/redux/modules/types';
import AppRegion from '@/types/app-region';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';

// Utils
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import notification from '@/utils/notification';

type StateProp = {
  user?: Nullable<User>;
  comparison?: Nullable<Comparison>;
};

type State = {
  metrics: PortfolioShortMetrics;
  loading: boolean;
};

type DispatchProps = {
  queryPortfolioMetricsById: (endpointEntity: string, id: string, params?: Record<string, string>, body?: Portfolio) => Promise<PortfolioShortMetrics>;
};

export type OwnProps<T> = {
  className?: string;
  portfolio: T;
  transformPortfolioToComparisonItem: (appRegion: AppRegion, validateIncomeStream: boolean) => ComparisonItem;
  displayedMetrics: PORTFOLIO_METRICS_FIELDS[];
  endpointEntity: string;
  queryParams?: Record<string, string>;
};

type PortfolioLinePerformanceProps<T> = StateProp & DispatchProps & OwnProps<T>;

export class PortfolioLinePerformance<T extends Portfolio> extends Component<PortfolioLinePerformanceProps<T>, State> {
  constructor(props: PortfolioLinePerformanceProps<T>) {
    super(props);
    this.state = {
      loading: true,
      metrics: {},
    };
  }

  componentDidMount() {
    this.getMetrics();
  }

  getMetrics = async () => {
    const { endpointEntity, portfolio, queryParams, queryPortfolioMetricsById, comparison, user } = this.props;
    if (!portfolio._id) {
      return;
    }

    let defaultMonthEndDataOption = 'undefined';
    if (user?.preferences != null) {
      const performanceAndRiskData = user.preferences.comparisonSettings?.performanceAndRiskData;
      if (performanceAndRiskData === PERIOD_END_DATA_OPTIONS.AUTO && user.preferences.autoPerformanceAndRiskData != null) {
        defaultMonthEndDataOption = user.preferences.autoPerformanceAndRiskData;
      } else if (performanceAndRiskData != null) {
        defaultMonthEndDataOption = performanceAndRiskData;
      }
    }

    const params = {
      ...queryParams,
      monthEndDataOption: comparison?.monthEndDataOption || defaultMonthEndDataOption,
    };

    // If portfolio is post message and POST message permission, add body to request
    let body;
    if (userHasPermission(user, PERMISSIONS.POST_MESSAGE) && portfolio.isFromPostMessage) {
      body = {
        ...portfolio,
      } as Portfolio;
    }

    queryPortfolioMetricsById(endpointEntity, portfolio._id, params, body)
      .then(data => {
        this.setState({
          metrics: {
            dates: get(data, 'dates'),
            size: data.size,
            oneYear: get(data, 'performance.percentGrowth["1Y"]'),
            threeYears: get(data, 'performance.percentGrowth["3Y"]'),
            fiveYears: get(data, 'performance.percentGrowth["5Y"]'),
            totalCost: data.totalCost,
          },
        });
      })
      .catch(() => {
        notification.error(formatMessage('PortfolioLinePerformance.Errors.FetchMetrics'));
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  render() {
    const { className, displayedMetrics, transformPortfolioToComparisonItem, user } = this.props;
    const { metrics, loading } = this.state;
    const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    const validateIncomeStream = user != null && userHasPermission(user, PERMISSIONS.INCOME_STREAM);

    if (loading) {
      return (
        <div className={classNames(className, 'portfolio-line-performance-container')}>
          <div className="portfolio-line-loading">
            <LoadingSpinner />
          </div>
        </div>
      );
    }
    const portfolioAsComparisonItem = transformPortfolioToComparisonItem(appRegion, validateIncomeStream);

    return (
      <div className={classNames(className, 'portfolio-line-performance')}>
        <div className="portfolio-line-performance-container">
          {metrics.size != null && displayedMetrics.includes(PORTFOLIO_METRICS_FIELDS.SIZE) && (
            <div className="performance-stat-container">
              <span className="performance-stat-item item-name">{formatMessage('PortfolioLinePerformance.Headers.Size')}</span>
              <span className="performance-stat-item item-value">
                <CurrencyCell value={{ value: metrics.size }} decimals={0} />
              </span>
            </div>
          )}
          {metrics.holdings && displayedMetrics.includes(PORTFOLIO_METRICS_FIELDS.HOLDINGS) && (
            <div className="performance-stat-container">
              <span className="performance-stat-item item-name">{formatMessage('PortfolioLinePerformance.Headers.Holdings')}</span>
              <span className="performance-stat-item item-value">{metrics.holdings}</span>
            </div>
          )}
          {displayedMetrics.includes(PORTFOLIO_METRICS_FIELDS.YEAR_ONE) && (
            <div className="performance-stat-container">
              <span className="performance-stat-item item-name">{formatMessage('PortfolioLinePerformance.Headers.OneYear')}</span>
              <GrowthCell className="performance-stat-item" value={metrics.oneYear} />
            </div>
          )}
          {displayedMetrics.includes(PORTFOLIO_METRICS_FIELDS.YEAR_THREE) && (
            <div className="performance-stat-container">
              <span className="performance-stat-item item-name">{formatMessage('PortfolioLinePerformance.Headers.ThreeYears')}</span>
              <GrowthCell className="performance-stat-item" value={metrics.threeYears} />
            </div>
          )}
          {displayedMetrics.includes(PORTFOLIO_METRICS_FIELDS.YEAR_FIVE) && (
            <div className="performance-stat-container">
              <span className="performance-stat-item item-name">{formatMessage('PortfolioLinePerformance.Headers.FiveYears')}</span>
              <GrowthCell className="performance-stat-item" value={metrics.fiveYears} />
            </div>
          )}
          {displayedMetrics.includes(PORTFOLIO_METRICS_FIELDS.MER) && (
            <div className="performance-stat-container">
              <span className="performance-stat-item item-name">{formatMessage('PortfolioLinePerformance.Headers.TotalCost')}</span>
              <PercentageCell className="performance-stat-item" value={metrics.totalCost} />
            </div>
          )}
        </div>
        <div className="portfolio-line-disclaimer">
          <FormattedMessage id="Disclaimers.NET_PERFORMANCE" />
          <PerformanceDisclaimer
            showItemNames={false}
            items={[
              {
                ...portfolioAsComparisonItem,
                dates: {
                  ...portfolioAsComparisonItem.dates,
                  portfolioGrowth: metrics.dates && metrics.dates.growth,
                },
              },
            ]}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  comparison: state.comparison.activeComparison,
});

export default connect(mapStateToProps, {
  queryPortfolioMetricsById,
})(PortfolioLinePerformance);
